import { Stack } from '@mui/material';
import { styled, SxProps, Theme } from '@mui/material/styles';

import { colors, theme } from '../../components';

// DashboardMakePayment styles
export const contractListContainerSx = {
    borderRadius: 2,
    overflowY: 'auto' as const,
    [theme.breakpoints.down('md')]: {
        padding: '0px 16px',
    },
};

export const iconTrashSx = {
    fontSize: 16,
    fontWeight: 600,
};

export const noContractFoundSx = {
    p: 2,
    textAlign: 'center' as const,
};

export const paymentContentBoxSx = {
    backgroundColor: colors.fullWhite,
    borderRadius: 3,
    p: 3,
};

export const bankAccountSelectSx = {
    '& .MuiInputBase-input': {
        '& .MuiTypography-root': {
            pr: 2,
        },
    },
};

export const deleteRowButtonSx = {
    backgroundColor: colors.errorPreset[20],
    borderRadius: 2,
    p: 1,
    color: colors.errorPreset[10],
    '&:hover': {
        backgroundColor: colors.errorPreset[20],
    },
};

// Columns: Contract ID, Payment Account, Payment Date, Amount Due, Scheduled, Autopay, Action.
// minWidth keeps every cell readable; below it the wrapper scrolls horizontally (the Action column is sticky).
export const paymentTableSx = {
    '& .MuiTable-root': {
        tableLayout: 'fixed',
        minWidth: 1180,
        '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root': {
            '&:nth-of-type(1)': { width: '10%' }, // Contract ID
            '&:nth-of-type(2)': { width: '18%' }, // Payment Account
            '&:nth-of-type(3)': { width: '14%' }, // Payment Date
            '&:nth-of-type(4)': { width: '22%' }, // Amount Due
            '&:nth-of-type(5)': { width: '12%' }, // Scheduled
            '&:nth-of-type(6)': { width: '15%' }, // Autopay
            '&:nth-of-type(7)': { width: '9%', textAlign: 'center' as const }, // Action
        },
    },
};

// Card payments hide the Payment Date column, leaving 6 columns
// (Contract ID, Payment Card, Amount Due, Scheduled, Autopay, Action).
export const paymentTableCardSx = {
    '& .MuiTable-root': {
        tableLayout: 'fixed',
        minWidth: 1100,
        '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root': {
            '&:nth-of-type(1)': { width: '11%' }, // Contract ID
            '&:nth-of-type(2)': { width: '26%' }, // Payment Card
            '&:nth-of-type(3)': { width: '25%' }, // Amount Due
            '&:nth-of-type(4)': { width: '13%' }, // Scheduled
            '&:nth-of-type(5)': { width: '16%' }, // Autopay
            '&:nth-of-type(6)': { width: '9%', textAlign: 'center' as const }, // Action
        },
    },
};

export const emptyStateBoxSx = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80%',
    width: '100%',
    my: 3,
};

export const paymentFooterSx = {
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 2,
    pr: 4,
};

export const cancelButtonSx = {
    width: 'fit-content',
};

export const payButtonSx = {
    width: 'fit-content',
    minWidth: '80px',
};

// Info box between selectable and unselectable (AutoPay) contracts
export const autopayInfoBoxSx = {
    borderRadius: 2,
    m: '6px 12px',
    p: '8px 12px',
    bgcolor: colors.bluePreset[50],
    display: 'flex',
    gap: 1,
    color: colors.bluePreset[30],
    alignItems: 'center',
};

/** Info note shown when Card is selected as the payment method, reminding of the $300 minimum */
export const cardMinimumAmountInfoBoxSx = {
    borderRadius: 2,
    m: '6px 12px',
    p: '8px 12px',
    bgcolor: colors.bluePreset[50],
    display: 'flex',
    gap: 1,
    color: colors.bluePreset[30],
    alignItems: 'center',
};

/** Error note shown when card processing fees fail to load */
export const cardFeesErrorBoxSx = {
    borderRadius: 2,
    m: '6px 12px',
    p: '8px 12px',
    bgcolor: colors.errorPreset[20],
    display: 'flex',
    gap: 1,
    color: colors.errorPreset[10],
    alignItems: 'center',
};

/** Info note shown below a payment row when a regular payment is already scheduled */
export const scheduledPaymentInfoNoteSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    borderRadius: 2,
    p: 1.25,
    bgcolor: colors.warningPreset[60],
    color: colors.warningPreset[50],
    border: `1px solid ${colors.warningPreset[80]}`,
};

export const confirmationModalContentSx = {
    py: 1,
};

export const confirmationTableSx = {
    mb: 2,
};

export const countrySelectSx = {
    borderRadius: 2,
    bgcolor: colors.fullWhite,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.greyPreset[20],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.greyPreset[20],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primaryLight[100],
    },
};

export const drawerFooterSx = {
    gap: 2,
    justifyContent: 'flex-end',
    pt: 3,
};

export const searchIconSx = (theme: { palette: { text: { secondary: string } } }) => ({
    color: theme.palette.text.secondary,
    fontSize: 16,
});

export const actionsCellSx = {
    display: 'flex',
    justifyContent: 'center' as const,
};

export const confirmationAuthTextSx = {
    color: colors.fullBlack,
    lineHeight: 1.6,
};

export const getConfirmationHighlightSx = (theme: { palette: { text: { secondary: string } } }) => ({
    color: theme.palette.text.secondary,
    mr: 0.5,
});

export const confirmationInfoTextSx = (theme: { palette: { text: { secondary: string } } }) => ({
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
});

export const confirmationTransactionTextSx = {
    lineHeight: 1.6,
    fontWeight: 600,
    fontSize: 16,
};

export const bankAccountModalContentSx = {
    py: 2,
};

export const drawerButtonSx = {
    width: 'fit-content',
};

// Contract list item - accepts isSelected for dynamic styling
export const getContractListItemSx = (isSelected: boolean) => ({
    p: 1.5,
    cursor: 'pointer',
    bgcolor: isSelected ? colors.primaryLight[5] : 'transparent',
    [theme.breakpoints.up('md')]: {
        borderLeft: isSelected ? `1px solid ${colors.primaryLight[100]}` : 'none',
    },
    '&:hover': {
        backgroundColor: colors.primaryLight[5],
    },
});

export const PaymentSummaryCard = styled(Stack)(({ theme }) => ({
    backgroundColor: colors.primaryLight[5],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    height: '100%',
    '& .contentWrapper': {
        backgroundColor: colors.fullWhite,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(1.5),
    },
    '& .chartPercentage': {
        textAlign: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
    '& .paymentCardWraper': {
        backgroundColor: colors.fullWhite,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(2),
        height: '100%',
        width: '100%',
    },
    '& .selectMenuWrapper': {
        maxWidth: '180px',
        minWidth: 'fit-content',
        '& .MuiFormControl-root': {
            maxWidth: '180px',
            minWidth: '120px',
        },
    },
}));

export const PaymentSideBarWrapper = styled(Stack)(({ theme }) => ({
    backgroundColor: colors.fullWhite,
    height: '100%',
    width: 205,
    borderRight: `1px solid ${colors.greyPreset[20]}`,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        height: 'auto',
        borderRight: 'none',
    },
    // Contract rows below `md` are full-width expandable cards (see ContractSidebar /
    // MobilePaymentContractCard), not the old compact chip grid, so this stacks them in a single column.
    '& .contractListContainer': {
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        },
    },
}));

export const paymentFooterSxButtons = {
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'flex-end',
    },
};

export const addAccountButtonSx = {
    width: 'fit-content',
    minHeight: 40,
    minWidth: 140,
};

// Mobile "Pay via" segmented toggle track (Bank Transfer | Credit Card)
export const mobilePaymentMethodTrackSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: 0,
    bgcolor: colors.primaryLight[5],
    borderRadius: 2,
    p: 0.5,
};

export const mobilePaymentMethodSegmentSx = (isSelected: boolean): SxProps<Theme> => ({
    m: 0,
    flex: 1,
    justifyContent: 'center',
    py: 1,
    px: 1.5,
    borderRadius: 1.5,
    bgcolor: isSelected ? colors.fullWhite : 'transparent',
    boxShadow: isSelected ? '0px 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
});

export const paymentFooterLeftSx: SxProps<Theme> = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
};

// Mobile payment-rows accordion: one card per contract, replacing the horizontally-scrolling
// desktop table below the `md` breakpoint.
export const mobilePaymentRowCardSx = (isExpanded: boolean): SxProps<Theme> => ({
    borderRadius: 2,
    border: `1px solid ${isExpanded ? colors.primaryLight[100] : colors.greyPreset[20]}`,
    overflow: 'hidden',
    bgcolor: colors.fullWhite,
});

export const mobilePaymentRowChevronSx = (isExpanded: boolean): SxProps<Theme> => ({
    fontSize: 16,
    fontWeight: 600,
    color: colors.greyPreset[80],
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
    display: 'inline-block',
});

export const mobilePaymentRowAmountBadgeSx: SxProps<Theme> = {
    borderRadius: 1.5,
    px: 1.25,
    py: 0.5,
    bgcolor: colors.warningPreset[60],
    color: colors.warningPreset[50],
    border: `1px solid ${colors.warningPreset[80]}`,
};

export const mobilePaymentRowBodySx: SxProps<Theme> = {
    p: 1.5,
    gap: 1.5,
    borderTop: `1px solid ${colors.greyPreset[20]}`,
    mt: 1.5,
};

export const mobilePaymentRowRemoveButtonSx = {
    width: '100%',
    justifyContent: 'center',
    borderRadius: 2,
    border: `1px solid ${colors.errorPreset[30]}`,
    color: colors.errorPreset[10],
    bgcolor: colors.fullWhite,
    '&:hover': {
        bgcolor: colors.errorPreset[20],
        border: `1px solid ${colors.errorPreset[30]}`,
    },
};
